import { ReactNode, useMemo, useState } from 'react'
import {
  AssignmentViewMode,
  getEffectiveAssignmentViewMode,
} from '../../TeamPlans/components/assignmentTimeline'
import ActorTimelineReadonlyRow from '../../TeamPlans/components/ActorTimelineReadonlyRow'
import UnresolvedActorReplacementDialog from '../../TeamPlans/components/UnresolvedActorReplacementDialog'
import EventActivityPlanAbilityRow from './EventActivityPlanAbilityRow'
import RaidPlan, {
  RaidPlanEncounterAssignmentAbility,
} from 'app/frontend/types/custom/RaidPlan'
import {
  clearActorOverrideIgnored,
  clearActorOverrideReplacementActor,
  clearEncounterAssignmentAbilityOverrides,
  EventPlanOverridesByPlanId,
  getActorOverrideIgnored,
  getActorOverrideReplacementActor,
  getPlanActorOverrides,
  setActorOverrideCharacterId,
  setActorOverrideIgnored,
  setActorOverrideReplacementActor,
  setCastTargetEntityOverride,
  setEncounterAssignmentAbilityHidden,
  setEncounterAssignmentEntityOverride,
} from './eventActivityPlanOverrides'
import {
  ResolvedAssignmentEntity,
  ResolvedAssignmentParticipant,
  ResolvedTimelineActor,
} from './eventActivityTimelineResolver'
import { EncounterAbilityRow } from '../../TeamPlans/hooks/useTeamPlanComputedData'
import { PhaseDivider } from '../../TeamPlans/components/AssignmentPhaseTimestampBadge'

type SortedAbilityRow = {
  abilityKey: string
  row: EncounterAbilityRow
  ability: RaidPlanEncounterAssignmentAbility
  isHidden: boolean
}

type Props = {
  raidPlan: RaidPlan
  planListPathPrefix: string
  rosterMemberships: wowaudit.TeamMembership[]
  planOverrides: EventPlanOverridesByPlanId
  onPlanOverridesChange: (next: EventPlanOverridesByPlanId) => void
  sortedAbilityRows: SortedAbilityRow[]
  selectedRows: EncounterAbilityRow[]
  phaseDividers: PhaseDivider[]
  selectedParticipants: ResolvedAssignmentParticipant[]
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>
  resolvedActors: ResolvedTimelineActor[]
  resolvedCastTargetsByCastId: Record<string, ResolvedAssignmentEntity[]>
  cooldownMaxTime: number
  canEdit: boolean
  isSaving: boolean
  assignmentHeaderActions?: ReactNode
  cooldownHeaderActions?: ReactNode
}

const ResolvedPlanPreviewContent = ({
  raidPlan,
  planListPathPrefix,
  rosterMemberships,
  planOverrides,
  onPlanOverridesChange,
  sortedAbilityRows,
  selectedRows,
  phaseDividers,
  selectedParticipants,
  resolvedByScopeBoxKey,
  resolvedActors,
  resolvedCastTargetsByCastId,
  cooldownMaxTime,
  canEdit,
  isSaving,
  assignmentHeaderActions,
  cooldownHeaderActions,
}: Props) => {
  const [replacementDialogActorUuid, setReplacementDialogActorUuid] = useState<
    string | null
  >(null)
  const [replacementDialogFallbackActor, setReplacementDialogFallbackActor] =
    useState<null | RaidPlan['timeline']['actors'][number]>(null)

  const teamMemberNameByCharacterId = useMemo(
    () =>
      Object.fromEntries(
        rosterMemberships.map((membership) => [
          membership.characterReference.id,
          membership.characterReference.name,
        ]),
      ),
    [rosterMemberships],
  )
  const actorOverrides = useMemo(
    () => getPlanActorOverrides(planOverrides, raidPlan.id),
    [planOverrides, raidPlan.id],
  )
  const unresolvedActorForDialog = useMemo(
    () =>
      replacementDialogActorUuid
        ? raidPlan.timeline.actors.find(
            (actor) => actor.uuid === replacementDialogActorUuid,
          )
        : undefined,
    [raidPlan.timeline.actors, replacementDialogActorUuid],
  )
  const replacementActorOverride = useMemo(() => {
    if (!replacementDialogActorUuid) return undefined
    return getActorOverrideReplacementActor({
      actorOverrides,
      actorUuid: replacementDialogActorUuid,
    })
  }, [actorOverrides, replacementDialogActorUuid])
  const replacementActorIsIgnored = useMemo(() => {
    if (!replacementDialogActorUuid) return false
    return getActorOverrideIgnored({
      actorOverrides,
      actorUuid: replacementDialogActorUuid,
    })
  }, [actorOverrides, replacementDialogActorUuid])

  return (
    <>
      {selectedRows.length > 0 ? (
        <div className='flex items-center justify-between mt-6 mb-2'>
          <h4>Assignments</h4>
          {assignmentHeaderActions}
        </div>
      ) : null}

      {sortedAbilityRows.map(({ abilityKey, row, ability, isHidden }) => {
        const abilityViewMode: AssignmentViewMode =
          getEffectiveAssignmentViewMode(ability.view_mode)
        return (
          <EventActivityPlanAbilityRow
            key={`${abilityKey}:${abilityViewMode}:${
              isHidden ? 'hidden' : 'visible'
            }`}
            row={row}
            ability={ability}
            phaseDividers={phaseDividers}
            resolvedByScopeBoxKey={resolvedByScopeBoxKey}
            selectedParticipants={selectedParticipants}
            isAbilityHidden={isHidden}
            isReadonly={!canEdit}
            onOverrideSelect={(selection) => {
              const nextPlanOverrides = setEncounterAssignmentEntityOverride({
                planOverrides,
                planId: raidPlan.id,
                abilityKey: selection.abilityKey,
                castIds: selection.castIds,
                boxId: selection.boxId,
                entityId: selection.entityId,
                override: selection.override,
              })
              onPlanOverridesChange(nextPlanOverrides)
            }}
            onSetAbilityHidden={(abilityKey, hidden) => {
              const nextPlanOverrides = setEncounterAssignmentAbilityHidden({
                planOverrides,
                planId: raidPlan.id,
                abilityKey,
                hidden,
              })
              onPlanOverridesChange(nextPlanOverrides)
            }}
            onResetAbilityOverrides={(abilityKey) => {
              const nextPlanOverrides =
                clearEncounterAssignmentAbilityOverrides({
                  planOverrides,
                  planId: raidPlan.id,
                  abilityKey,
                })
              onPlanOverridesChange(nextPlanOverrides)
            }}
          />
        )
      })}

      {resolvedActors.length > 0 ? (
        <>
          <div className='flex items-center justify-between mb-2 mt-6'>
            <h4 className='m-0'>Cooldowns</h4>
            {cooldownHeaderActions}
          </div>
          <ActorTimelineReadonlyRow
            actors={resolvedActors.map((resolvedActor) => {
              const preferredCharacterName =
                typeof resolvedActor.actor.preferred_character_id === 'number'
                  ? teamMemberNameByCharacterId[
                      resolvedActor.actor.preferred_character_id
                    ]
                  : undefined
              const plannedLabel =
                preferredCharacterName ?? resolvedActor.actor.label
              return {
                actor: resolvedActor.actor,
                label: resolvedActor.participant?.name ?? plannedLabel,
                plannedLabel,
                isUnresolved: !resolvedActor.participant,
                isIgnored: resolvedActor.isIgnored === true,
                hasReplacementOverride: Boolean(
                  actorOverrides?.actors?.[resolvedActor.actor.uuid]
                    ?.replacementActor,
                ),
                resolvedCharacterId: resolvedActor.participant?.characterId,
                isOverridden: resolvedActor.isOverridden === true,
              }
            })}
            generalCasts={raidPlan.timeline.general?.casts ?? []}
            selectedParticipants={selectedParticipants}
            onActorOverrideSelect={(selection) => {
              const withCharacterOverride = setActorOverrideCharacterId({
                planOverrides,
                planId: raidPlan.id,
                actorUuid: selection.actorUuid,
                characterId: selection.characterId,
              })
              const nextPlanOverrides = clearActorOverrideIgnored({
                planOverrides: withCharacterOverride,
                planId: raidPlan.id,
                actorUuid: selection.actorUuid,
              })
              onPlanOverridesChange(nextPlanOverrides)
            }}
            onActorIgnoreSelect={(actorUuid) => {
              const nextPlanOverrides = setActorOverrideIgnored({
                planOverrides,
                planId: raidPlan.id,
                actorUuid,
                ignored: true,
              })
              onPlanOverridesChange(nextPlanOverrides)
            }}
            onUnresolvedActorClick={
              canEdit
                ? (actor) => {
                    setReplacementDialogActorUuid(actor.uuid)
                    setReplacementDialogFallbackActor(actor)
                  }
                : undefined
            }
            maxTime={cooldownMaxTime}
            resolvedCastTargetsByCastId={resolvedCastTargetsByCastId}
            onCastTargetOverrideSelect={(selection) => {
              const nextPlanOverrides = setCastTargetEntityOverride({
                planOverrides,
                planId: raidPlan.id,
                castId: selection.castId,
                entityId: selection.entityId,
                override: selection.override,
              })
              onPlanOverridesChange(nextPlanOverrides)
            }}
            canEditCastTargets={canEdit}
          />
          {replacementDialogFallbackActor ? (
            <UnresolvedActorReplacementDialog
              open={Boolean(replacementDialogFallbackActor)}
              onOpenChange={(open) => {
                if (open) return
                setReplacementDialogActorUuid(null)
                setReplacementDialogFallbackActor(null)
              }}
              planListPathPrefix={planListPathPrefix}
              rosterMemberships={rosterMemberships}
              raidPlan={raidPlan}
              originalActor={
                unresolvedActorForDialog ?? replacementDialogFallbackActor
              }
              replacementActor={replacementActorOverride}
              isIgnored={replacementActorIsIgnored}
              isSaving={isSaving}
              participants={selectedParticipants}
              onReplacementActorChange={(replacementActor) => {
                if (!replacementDialogActorUuid) return
                const replacementOverrides = replacementActor
                  ? setActorOverrideReplacementActor({
                      planOverrides,
                      planId: raidPlan.id,
                      actorUuid: replacementDialogActorUuid,
                      replacementActor,
                    })
                  : clearActorOverrideReplacementActor({
                      planOverrides,
                      planId: raidPlan.id,
                      actorUuid: replacementDialogActorUuid,
                    })
                const nextPlanOverrides = replacementActor
                  ? clearActorOverrideIgnored({
                      planOverrides: replacementOverrides,
                      planId: raidPlan.id,
                      actorUuid: replacementDialogActorUuid,
                    })
                  : replacementOverrides
                onPlanOverridesChange(nextPlanOverrides)
              }}
              onIgnoredChange={(ignored) => {
                if (!replacementDialogActorUuid) return
                const nextPlanOverrides = ignored
                  ? setActorOverrideIgnored({
                      planOverrides,
                      planId: raidPlan.id,
                      actorUuid: replacementDialogActorUuid,
                      ignored: true,
                    })
                  : clearActorOverrideIgnored({
                      planOverrides,
                      planId: raidPlan.id,
                      actorUuid: replacementDialogActorUuid,
                    })
                onPlanOverridesChange(nextPlanOverrides)
                if (!ignored) return
                setReplacementDialogActorUuid(null)
                setReplacementDialogFallbackActor(null)
              }}
            />
          ) : null}
        </>
      ) : null}
    </>
  )
}

export default ResolvedPlanPreviewContent
